import React from 'react';
import { observer } from 'mobx-react-lite';

import { GameCategory } from '../../../enums';
import { SlotHistoryFrame } from './slot-history';
import { SidebarMenu } from './sideBar-menu';
import { useStore } from '../../../hooks';
import { Tournament } from './tournament';
import { FreeDrop } from './free-drop';
import { LobbyFrame } from './lobby';
import { PGWheel } from './pg-wheel';
import { Rewards } from './rewards';
import { Jackpot } from './jackpot';
import FreeSpin from './free-spin';
import { Promo } from './promo';
import { Popup } from './popup';

interface FeaturesProps { }

export const Features: React.FC<FeaturesProps> = observer(() => {
  const { gameStore, freeSpinStore, cashbackStore, modalStore, slotHistoryStore, lobbyStore, freeDropStore, boostedJackpotStore, hubStore, sidebarMenuStore, maintenanceStore } = useStore();
  // Entry gate, not live state: hide promos when the hub never came up, keep them through a drop.
  const { everConnected: hubReady } = hubStore;
  const { isSlotHistoryOpen: isOpenSlotsHistory } = slotHistoryStore;
  const { popupFeatureOpen, gameInterruptionModalState: gameInterruptionModalSate } = modalStore;
  const { launchData: { data: launchData } } = gameStore;
  const { visibleFreeSpin, visibleFreeSpins, freeSpinAllRewardsOpen } = freeSpinStore;
  const { freeDropInfo } = freeDropStore;
  const { showCashback } = cashbackStore;
  const { isLobbyOpen } = lobbyStore;
  const { showSidebar: showSidebarMenu, showPascalPromotions } = sidebarMenuStore;

  // These four are fed entirely by hub pushes, so a hub that never connected leaves them with nothing
  // to show. Once it has connected they stay: a mid-session drop must not yank them off a live game.
  const hasJackpot = !!launchData?.hasJackpot && hubReady;
  const hasPromo = !!launchData?.promoInfo;
  const hasPopup = !!launchData?.popUps?.length;
  const hasPreLobby = !!launchData?.hasPreLobby;
  // A16 belt-and-braces: hubReady alone already keeps a live wheel mounted, but a wheel that is
  // triggered or on screen must never unmount — the game is paused and Spin is the only way out.
  const hasPGWheel = !!boostedJackpotStore.wheelInfo && (hubReady || boostedJackpotStore.isWheelInFlight);
  const hasTournament = !!launchData?.hasTournament;
  const hasFreebet = !!freeSpinStore.freeSpinInfo?.length;

  const showLobby = !!launchData?.hasLobby;
  const showTopWinners = !!gameStore.topWinnersEnabled;

  const showSidebarOpener = launchData?.category === GameCategory.Slot || launchData?.category === GameCategory.TronixZone;

  if (gameInterruptionModalSate.isOpen) return null;
  // Nothing floats over the maintenance page, whether launch data or a push opened it.
  if (maintenanceStore.isMaintenanceMode) return null;

  return (
    <>
      {/* Renders the first visible free spin, then advances the queue on close/use later */}
      {visibleFreeSpin && (
        <FreeSpin
          data={visibleFreeSpin}
          key={visibleFreeSpin.id}
          allData={visibleFreeSpins}
          initialShowRewards={freeSpinAllRewardsOpen}
          onClose={freeSpinStore.closeFreeSpin}
          onCloseAll={freeSpinStore.closeAllFreeSpins}
        />
      )}

      {(freeDropInfo && hubReady) ? <FreeDrop id={freeDropInfo.id} /> : null}
      <Tournament />
      {isOpenSlotsHistory && <SlotHistoryFrame />}
      {isLobbyOpen && <LobbyFrame />}
      {hasPromo && <Promo />}
      {
        showSidebarMenu && (
          <SidebarMenu
            hasPromo={hasPromo}
            showLobby={showLobby}
            hasJackpot={hasJackpot}
            hasFreebet={hasFreebet}
            hasPGWheel={hasPGWheel}
            hasPreLobby={hasPreLobby}
            hasTournament={hasTournament}
            hasTopWinners={showTopWinners}
            showSidebarOpener={showSidebarOpener}
            showPascalPromotions={showPascalPromotions}
          />
        )
      }
      {hasPopup && popupFeatureOpen && <Popup popups={launchData.popUps} />}
      {hasJackpot && <Jackpot />}
      {hasPGWheel && <PGWheel />}
      {(showCashback && hubReady) && <Rewards />}
    </>
  );
});
